import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Select, Button, Steps, message, Divider, Alert, Image, Switch, Row, Col } from 'antd';
import { PlusOutlined, DollarOutlined, RocketOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import ServerInstallProgress from '../../components/ServerInstallProgress';

const { Option } = Select;
const { Step } = Steps;

interface GameType {
  id: number;
  name: string;
  display_name: string;
  description: string;
  icon?: string;
  supports_mods?: boolean;
  is_steam_game?: boolean;
}

interface ServerNode {
  id: number;
  name: string;
  host: string;
  status: string;
  is_local: boolean;
  server_path: string;
}

interface ActivationCodeInfo {
  id: number;
  code: string;
  game_type_id: number;
  game_type_name: string;
  game_type_display_name: string;
  game_type_icon?: string;
  game_type_supports_mods?: boolean;
  node_id: number;
  node_name: string;
  node_host: string;
  max_players: number;
  billing_period: string;
  mod_support_enabled: boolean;
  price: number;
  total_discount: number;
}

const CreateServerWithActivationCode: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [activationCodeInfo, setActivationCodeInfo] = useState<ActivationCodeInfo | null>(null);
  const [activationCode, setActivationCode] = useState('');
  const [formData, setFormData] = useState<any>({});
  const [showProgress, setShowProgress] = useState(false);
  const [installingServerId, setInstallingServerId] = useState<number | null>(null);
  const [socket, setSocket] = useState<any>(null);
  const [modSupportEnabled, setModSupportEnabled] = useState(false);

  useEffect(() => {
    // 使用全局Socket.IO连接，避免重复连接
    const globalSocket = (window as any).io;
    if (globalSocket) {
      setSocket(globalSocket);
      console.log('使用全局Socket.IO连接:', globalSocket.id);
    } else {
      console.warn('全局Socket.IO连接不可用');
    }
  }, []);

  // 验证激活码
  const handleValidateCode = async (code: string) => {
    if (!code.trim()) {
      setActivationCodeInfo(null);
      return;
    }

    setValidating(true);
    try {
      const response = await api.post('/activation-server/validate', { code });
      
      if (response.data.success) {
        setActivationCodeInfo(response.data.data);
        // 设置MOD支持状态 - 根据激活码的设置来决定
        setModSupportEnabled(response.data.data.mod_support_enabled);
        // 设置表单字段值
        form.setFieldsValue({
          activation_code: code
        });
        message.success('激活码验证成功！可以继续创建服务器');
      }
    } catch (error: any) {
      setActivationCodeInfo(null);
      message.error(error.response?.data?.message || '激活码验证失败');
    } finally {
      setValidating(false);
    }
  };

  // 获取付费周期显示名称
  const getBillingPeriodName = (period: string) => {
    const periodMap: { [key: string]: string } = {
      'weekly': '周付',
      'monthly': '月付',
      'quarterly': '季付',
      'semiannual': '半年付',
      'yearly': '年付'
    };
    return periodMap[period] || period;
  };

  const onFinish = async (values: any) => {
    if (!activationCodeInfo) {
      message.error('请先验证激活码');
      return;
    }

    setLoading(true);
    
    try {
      console.log('激活码服务器创建 - onFinish接收到的values:', values);
      console.log('激活码信息:', activationCodeInfo);

      // 合并所有步骤的数据，使用激活码的数据
      const finalFormData = {
        name: values.name, // 用户输入的服务器名称
        game_type_id: activationCodeInfo.game_type_id,
        node_id: activationCodeInfo.node_id,
        max_players: activationCodeInfo.max_players,
        billing_period: activationCodeInfo.billing_period,
        mod_support_enabled: modSupportEnabled ? 1 : 0,
        // 激活码相关参数
        activation_code: activationCodeInfo.code,
        is_activation_code: true
      };

      console.log('发送的激活码服务器创建数据:', finalFormData);

      // 确保formData包含服务器名称，用于进度条显示
      setFormData((prev: any) => ({ ...prev, ...values }));

      // 先设置进度条监听器
      let serverId: number | null = null;
      
      const handleInstallProgress = (data: any) => {
        console.log('收到安装进度:', data);
        
        // 如果是第一次收到进度更新，显示进度条
        if (!showProgress && data.serverId) {
          serverId = data.serverId;
          setInstallingServerId(data.serverId);
          setShowProgress(true);
          console.log('显示进度条:', { serverId: data.serverId, serverName: values.name });
        }
        
        // 检查是否完成
        if (data.status === 'success') {
          // 不自动关闭进度条，让用户点击按钮
          message.success('服务器创建成功！');
          socket?.off('server-install-progress', handleInstallProgress);
        } else if (data.status === 'error') {
          setShowProgress(false);
          message.error(data.message || '服务器创建失败');
          socket?.off('server-install-progress', handleInstallProgress);
        }
      };
      
      if (socket) {
        socket.on('server-install-progress', handleInstallProgress);
        
        // 清理监听器
        setTimeout(() => {
          socket.off('server-install-progress', handleInstallProgress);
        }, 300000); // 5分钟后清理
      }

      // 发送API请求到激活码创建接口
      const response = await api.post('/activation-server/create', {
        code: activationCodeInfo.code,
        server_name: values.name,
        mod_support_enabled: modSupportEnabled ? 1 : 0
      }, {
        timeout: 60000 // 60秒超时
      });
      
      console.log('API响应:', response.data);
      
      // 如果API立即返回了serverId，也设置进度条
      if (response.data.serverId && !showProgress) {
        serverId = response.data.serverId;
        setInstallingServerId(response.data.serverId);
        setShowProgress(true);
        console.log('API响应后显示进度条:', { serverId: response.data.serverId, serverName: values.name });
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || '创建服务器失败');
    } finally {
      setLoading(false);
    }
  };

  const next = () => {
    form.validateFields().then((values) => {
      console.log('步骤切换时的表单值:', values);
      // 保存当前步骤的数据到formData
      setFormData((prev: any) => ({ ...prev, ...values }));
      setCurrentStep(currentStep + 1);
    }).catch((errorInfo) => {
      console.log('表单验证失败:', errorInfo);
      message.error('请完成当前步骤的所有必填字段');
    });
  };

  const prev = () => {
    setCurrentStep(currentStep - 1);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <Alert
              message="激活码验证"
              description="请输入您的激活码来获取服务器配置信息"
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Form.Item
              name="activation_code"
              label="激活码"
              rules={[{ required: !activationCodeInfo, message: '请输入激活码' }]}
            >
              <Input.Group compact>
                <Input
                  placeholder="请输入激活码"
                  size="large"
                  value={activationCode}
                  onChange={(e) => setActivationCode(e.target.value)}
                  readOnly={!!activationCodeInfo}
                  style={{ width: 'calc(100% - 80px)' }}
                />
                <Button
                  size="large"
                  onClick={() => handleValidateCode(activationCode)}
                  disabled={!!activationCodeInfo || validating}
                  loading={validating}
                  style={{ width: 80 }}
                >
                  验证
                </Button>
              </Input.Group>
            </Form.Item>

            {activationCodeInfo && (
              <Alert
                message="激活码验证成功"
                description="激活码验证通过，您可以继续创建服务器"
                type="success"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {activationCodeInfo && (
              <Card>
                <div className="text-center">
                  <h3 className="text-lg font-medium mb-4">激活码信息</h3>
                  
                  <div className="flex items-center justify-center space-x-4 mb-4">
                    <Image
                      src={activationCodeInfo.game_type_icon || '/images/game-icons/placeholder_184x69.jpg'}
                      alt={activationCodeInfo.game_type_display_name}
                      width={184}
                      height={69}
                      style={{ objectFit: 'cover', borderRadius: '8px' }}
                      fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTg0IiBoZWlnaHQ9IjY5IiB2aWV3Qm94PSIwIDAgMTg0IDY5IiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTg0IiBoZWlnaHQ9IjY5IiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjkyIiB5PSIzNCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+R2FtZTwvdGV4dD4KPC9zdmc+"
                    />
                  </div>

                  <Row gutter={[16, 16]} className="text-left">
                    <Col span={12}>
                      <div className="bg-gray-50 p-3 rounded">
                        <div className="text-sm text-gray-600">游戏类型</div>
                        <div className="font-medium">{activationCodeInfo.game_type_display_name}</div>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div className="bg-gray-50 p-3 rounded">
                        <div className="text-sm text-gray-600">最大玩家数</div>
                        <div className="font-medium">{activationCodeInfo.max_players}人</div>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div className="bg-gray-50 p-3 rounded">
                        <div className="text-sm text-gray-600">服务器节点</div>
                        <div className="font-medium">{activationCodeInfo.node_name}</div>
                        <div className="text-xs text-gray-500">{activationCodeInfo.node_host}</div>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div className="bg-gray-50 p-3 rounded">
                        <div className="text-sm text-gray-600">付费周期</div>
                        <div className="font-medium">{getBillingPeriodName(activationCodeInfo.billing_period)}</div>
                      </div>
                    </Col>
                  </Row>

                  {activationCodeInfo.game_type_supports_mods && (
                    <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
                      <Row gutter={16} align="middle">
                        <Col span={16}>
                          <div>
                            <h4 className="font-medium text-yellow-800 mb-1">MOD支持功能</h4>
                            <p className="text-yellow-600 text-sm mb-0">
                              {activationCodeInfo.mod_support_enabled 
                                ? '激活码包含MOD支持，您可以选择是否启用'
                                : '激活码不包含MOD支持，无法启用MOD功能'
                              }
                            </p>
                          </div>
                        </Col>
                        <Col span={8}>
                          <Switch
                            checked={modSupportEnabled}
                            onChange={setModSupportEnabled}
                            disabled={!activationCodeInfo.mod_support_enabled}
                            checkedChildren="启用"
                            unCheckedChildren="禁用"
                          />
                        </Col>
                      </Row>
                    </div>
                  )}

                  <div className="mt-4 p-3 bg-green-50 rounded">
                    <div className="text-sm text-green-600">
                      <div>原价: ¥{(Number(activationCodeInfo.price) || 0).toFixed(2)}</div>
                      <div className="font-bold text-lg">使用激活码实际支付: ¥0.00</div>
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <Alert
              message="服务器配置"
              description="请为您的服务器设置名称"
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Form.Item
              name="name"
              label="服务器名称"
              rules={[
                { required: true, message: '请输入服务器名称' },
                { min: 2, message: '服务器名称至少2个字符' },
                { max: 50, message: '服务器名称最多50个字符' }
              ]}
            >
              <Input placeholder="输入服务器名称" size="large" />
            </Form.Item>

            {activationCodeInfo && (
              <Card>
                <div className="text-center">
                  <h3 className="text-lg font-medium mb-2">最终配置确认</h3>
                  <div className="space-y-2 text-sm">
                    <div>游戏类型: {activationCodeInfo.game_type_display_name}</div>
                    <div>最大玩家数: {activationCodeInfo.max_players}人</div>
                    <div>服务器节点: {activationCodeInfo.node_name}</div>
                    <div>付费周期: {getBillingPeriodName(activationCodeInfo.billing_period)}</div>
                    {activationCodeInfo.game_type_supports_mods && (
                      <div>MOD支持: {modSupportEnabled ? '启用' : '禁用'}</div>
                    )}
                  </div>
                  <Divider />
                  <div className="text-2xl font-bold text-green-600">
                    ¥0.00
                  </div>
                  <p className="text-sm text-gray-500 mt-1">使用激活码实际支付</p>
                </div>
              </Card>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const steps = [
    {
      title: '激活码验证',
      icon: <PlusOutlined />,
    },
    {
      title: '服务器配置',
      icon: <DollarOutlined />,
    },
  ];

  return (
    <div className="fade-in" style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', minHeight: '100vh', padding: '20px' }}>
      <div style={{ width: '100%', maxWidth: '800px' }}>
        <Card>
          <h2 className="text-2xl font-bold mb-6" style={{ textAlign: 'center' }}>使用激活码创建服务器</h2>
          
          <Steps current={currentStep} className="mb-8">
            {steps.map((item, index) => (
              <Step key={index} title={item.title} icon={item.icon} />
            ))}
          </Steps>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            onValuesChange={(changedValues, allValues) => {
              console.log('表单值变化:', { changedValues, allValues });
            }}
            className="max-w-4xl"
          >
          <div className="min-h-96">
            {renderStepContent()}
          </div>

          <Divider />

          <div className="flex justify-between">
            {currentStep > 0 && (
              <Button onClick={prev}>
                上一步
              </Button>
            )}
            
            <div className="ml-auto">
              {currentStep < steps.length - 1 && (
                <Button 
                  type="primary" 
                  onClick={next}
                  disabled={!activationCodeInfo}
                >
                  下一步
                </Button>
              )}
              
              {currentStep === steps.length - 1 && (
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={loading}
                  disabled={!activationCodeInfo}
                  icon={<RocketOutlined />}
                >
                  创建服务器
                </Button>
              )}
            </div>
          </div>
        </Form>
        </Card>
      </div>
      
      <ServerInstallProgress
        visible={showProgress}
        onClose={() => setShowProgress(false)}
        serverId={installingServerId}
        serverName={formData.name || 'ARK服务器'}
        socket={socket}
      />
    </div>
  );
};

export default CreateServerWithActivationCode;
